import React, { useState, useRef, useEffect } from 'react';
import { MagnifyingGlassIcon, ChevronDownIcon, XMarkIcon, MapPinIcon } from '@heroicons/react/24/outline';
import tagsData from '../data/tags.json';

const SearchUI = ({
  searchTerm,
  setSearchTerm,
  selectedTags,
  setSelectedTags,
  tags = tagsData.tags, // Use master tag list as default
  placeholder = "Search",
  searchPlaceholder = "Search",
  className = "",
  onSearch,
  onClearAll,
  onTagToggle = null,
  allowMultipleTags = false,
  showArchetypeFilter = false, // New prop to show archetype filter
  selectedArchetype = 'all', // 'all', 'event', 'site'
  onArchetypeChange = null, // Callback for archetype changes
  mapAreaFilterActive = false, // Whether map area filter is active
  onClearMapAreaFilter = null // Callback to clear map area filter
}) => {
  const [showMobileOverlay, setShowMobileOverlay] = useState(false);
  const [showDesktopDropdown, setShowDesktopDropdown] = useState(false);
  const [filterSearchTerm, setFilterSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const filterInputRef = useRef(null);

  // Archetype filter options (special tags at the top)
  const archetypeOptions = [
    { id: 'all', label: 'All', icon: '🌐', description: 'Show all project types' },
    { id: 'site', label: 'Ongoing', icon: '📍', description: 'Ongoing projects and contexts' },
    { id: 'event', label: 'One-Off', icon: '🎯', description: 'One-time events' }
  ];

  // Filter tags based on search term
  const filteredTags = tags.filter(tag => 
    tag.label.toLowerCase().includes(filterSearchTerm.toLowerCase())
  );
  
  // Filter archetype options based on search term (if searching)
  const filteredArchetypes = showArchetypeFilter 
    ? archetypeOptions.filter(arch => 
        arch.label.toLowerCase().includes(filterSearchTerm.toLowerCase()) ||
        arch.description.toLowerCase().includes(filterSearchTerm.toLowerCase())
      )
    : [];

  // Handle mobile search focus
  const handleMobileSearchFocus = () => {
    setShowMobileOverlay(true);
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      if (showMobileOverlay) {
        // On mobile overlay, trigger search
        handleMobileSearchClick();
      } else {
        // On desktop, trigger search
        if (onSearch) onSearch();
      }
    }
  };

  // Handle mobile search button click
  const handleMobileSearchClick = () => {
    if (onSearch) onSearch();
    setShowMobileOverlay(false);
  };

  // Handle mobile clear all button click
  const handleMobileClearAllClick = () => {
    setSearchTerm('');
    setSelectedTags([]);
    if (onClearAll) onClearAll();
    setShowMobileOverlay(false);
  };

  // Handle archetype selection
  const handleArchetypeSelect = (archetypeId) => {
    if (onArchetypeChange) {
      onArchetypeChange(archetypeId);
    }
  };

  // Handle tag selection
  const toggleTag = (tagId) => {
    // Check if this is an archetype filter (special handling)
    if (showArchetypeFilter && ['all', 'event', 'site'].includes(tagId)) {
      handleArchetypeSelect(tagId);
      return;
    }
    
    if (onTagToggle) {
      // Use custom tag toggle handler (for CommunityPage)
      onTagToggle(tagId);
    } else if (allowMultipleTags) {
      // Allow multiple tags (CommunityPage behavior)
      setSelectedTags(prev => 
        prev.includes(tagId) 
          ? prev.filter(id => id !== tagId) // Remove tag
          : [...prev, tagId] // Add tag
      );
    } else {
      // Single tag selection (DiscoverPage behavior)
      setSelectedTags(prev => 
        prev.includes(tagId) 
          ? [] // Deselect if already selected
          : [tagId] // Select only this tag, deselecting all others
      );
    }
  };

  // Close dropdown when clicking outside (desktop only)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDesktopDropdown(false);
        setFilterSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getSelectedTagsText = () => {
    // If archetype filter is active and not 'all', show it
    if (showArchetypeFilter && selectedArchetype !== 'all') {
      const archetype = archetypeOptions.find(a => a.id === selectedArchetype);
      if (selectedTags.length === 0) {
        return archetype ? archetype.label : "Filter by type";
      }
      return `${archetype ? archetype.label : 'Type'} + ${selectedTags.length} tag${selectedTags.length !== 1 ? 's' : ''}`;
    }
    
    if (selectedTags.length === 0) return "Filter";
    if (selectedTags.length === 1) {
      const tag = tags.find(t => t.id === selectedTags[0]);
      return tag ? tag.label : "Filter";
    }
    return `${selectedTags.length} filters`;
  };

  const clearAllFilters = () => {
    setSelectedTags([]);
    setFilterSearchTerm('');
    if (showArchetypeFilter && onArchetypeChange) {
      onArchetypeChange('all');
    }
  };

  return (
    <>
      {/* Desktop Search */}
      <div className={`hidden lg:block ${className}`}>
        <div className="bg-white/90 dark:bg-gray-800/90 rounded-full shadow border border-gray-200 dark:border-gray-700 backdrop-blur-sm">
          <div className="flex items-center gap-1 px-1">
            {/* Search Input */}
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={handleKeyPress}
                className="block w-full pl-11 pr-5 py-2.5 text-sm bg-transparent text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-0 focus:border-transparent border-0"
              />
              {/* {mapAreaFilterActive && (
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-full text-xs font-medium">
                    <MapPinIcon className="w-3 h-3" />
                    Map Area
                  </span>
                  {onClearMapAreaFilter && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onClearMapAreaFilter();
                      }}
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"
                      aria-label="Clear map area filter"
                    >
                      <XMarkIcon className="w-3 h-3" />
                    </button>
                  )}
                </div>
              )} */}
            </div>
            
            {/* Vertical Divider */}
            <div className="w-px h-9 bg-gray-200 dark:bg-gray-600"></div>
            
            {/* Filter Section */}
            <div className="flex-1 max-w-[35%]">
              <div className="relative" ref={dropdownRef}>
                {/* Filter Button */}
                <button
                  onClick={() => {
                    setShowDesktopDropdown(!showDesktopDropdown);
                    if (!showDesktopDropdown && filterInputRef.current) {
                      setTimeout(() => filterInputRef.current.focus(), 100);
                    }
                  }}
                  className="flex items-center justify-between w-full px-5 py-2.5 text-left text-sm text-gray-900 dark:text-white leading-5 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none transition-colors duration-200 border-0 rounded-none shadow-none bg-transparent"
                >
                  <span className={`truncate ${selectedTags.length > 0 ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>
                    {getSelectedTagsText()}
                  </span>
                  <div className="flex items-center gap-2 h-5">
                    {(selectedTags.length > 0 || (showArchetypeFilter && selectedArchetype !== 'all')) && (
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          clearAllFilters();
                        }}
                        className="p-0.5 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-full transition-colors h-4 w-4 flex items-center justify-center cursor-pointer"
                      >
                        <XMarkIcon className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                      </div>
                    )}
                    <ChevronDownIcon 
                      className={`w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${
                        showDesktopDropdown ? 'rotate-180' : ''
                      }`} 
                    />
                  </div>
                </button>

                {/* Filter Dropdown Menu */}
                {showDesktopDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-4 md:mt-1 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl shadow-lg z-50 max-h-80 overflow-hidden">
                    {/* Search Input */}
                    <div className="p-3 border-b border-gray-200 dark:border-gray-600">
                      <input
                        ref={filterInputRef}
                        type="text"
                        placeholder="Search tags..."
                        value={filterSearchTerm}
                        onChange={(e) => setFilterSearchTerm(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    {/* Tags List */}
                    <div className="max-h-60 overflow-y-auto">
                      {/* Archetype Filter Section (if enabled) */}
                      {showArchetypeFilter && (
                        <>
                          {/* <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-600">
                            <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                              Project Type
                            </div>
                          </div>
                          {archetypeOptions.map((arch) => {
                            const isSelected = selectedArchetype === arch.id;
                            return (
                              <button
                                key={arch.id}
                                onClick={() => toggleTag(arch.id)}
                                className={`
                                  w-full flex items-center gap-3 px-3 py-3 text-left text-sm
                                  hover:bg-gray-50 dark:hover:bg-gray-600
                                  transition-colors duration-150
                                  ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''}
                                `}
                              >
                                <span className="text-lg">{arch.icon}</span>
                                <div className="flex-1">
                                  <div className={`font-medium ${isSelected ? 'text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-white'}`}>
                                    {arch.label}
                                  </div>
                                  {arch.description && (
                                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                      {arch.description}
                                    </div>
                                  )}
                                </div>
                                {isSelected && (
                                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                )}
                              </button>
                            );
                          })} */}
                          <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-600">
                            <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                              Tags
                            </div>
                          </div>
                        </>
                      )}
                      
                      {/* Regular Tags */}
                      {filteredTags.length === 0 && (!showArchetypeFilter || filteredArchetypes.length === 0) ? (
                        <div className="p-3 text-sm text-gray-500 dark:text-gray-400 text-center">
                          No tags found
                        </div>
                      ) : (
                        filteredTags.map((tag) => {
                          const isSelected = selectedTags.includes(tag.id);
                          return (
                            <button
                              key={tag.id}
                              onClick={() => toggleTag(tag.id)}
                              className={`
                                w-full flex items-center gap-3 px-3 py-3 text-left text-sm
                                hover:bg-gray-50 dark:hover:bg-gray-600
                                transition-colors duration-150
                                ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''}
                              `}
                            >
                              <span className="text-lg">{tag.icon}</span>
                              <div className="flex-1">
                                <div className={`font-medium ${isSelected ? 'text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-white'}`}>
                                  {tag.label}
                                </div>
                                {tag.description && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                    {tag.description}
                                  </div>
                                )}
                              </div>
                              {isSelected && (
                                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                              )}
                            </button>
                          );
                        })
                      )}
                    </div>

                    {/* Clear All Button */}
                    {(selectedTags.length > 0 || (showArchetypeFilter && selectedArchetype !== 'all')) && (
                      <div className="p-3 border-t border-gray-200 dark:border-gray-600">
                        <button
                          onClick={clearAllFilters}
                          className="w-full text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                        >
                          Clear all filters
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Search */}
      <div className="lg:hidden relative">
        {/* Normal search bar when not active */}
        {!showMobileOverlay && (
          <div className="flex flex-col items-start lg:items-center gap-3 lg:gap-0 my-3 px-4 md:px-0 lg:px-0">
            {/* Search Bar */}
            <div className="flex-1 relative lg:max-w-md w-full">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder={placeholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={handleMobileSearchFocus}
                onKeyPress={handleKeyPress}
                className="block w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-transparent rounded-full leading-5 bg-white text-sm dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 dark:focus:border-blue-400"
              />
            </div>
            {/* Filter Dropdown - Only show if filters are selected */}
            {/* {selectedTags.length > 0 && (
              <div className="w-full lg:w-64">
                <div className="relative">
                  <button
                    className="flex items-center justify-between w-full px-6 py-4 text-left text-base text-gray-900 dark:text-white leading-5 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none transition-colors duration-200 border border-gray-300 dark:border-gray-600 rounded-full bg-white dark:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <span className="truncate text-sm text-gray-900 dark:text-white">
                      {getSelectedTagsText()}
                    </span>
                    <div className="flex items-center gap-2 h-5">
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          clearAllFilters();
                        }}
                        className="p-0.5 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-full transition-colors h-4 w-4 flex items-center justify-center cursor-pointer"
                      >
                        <XMarkIcon className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                      </div>
                      <ChevronDownIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                    </div>
                  </button>
                </div>
              </div>
            )} */}
          </div>
        )}

        {/* Full screen search interface when active */}
        {showMobileOverlay && (
          <div className="fixed inset-0 bg-white dark:bg-gray-900 z-[60000] flex flex-col">
            {/* Header */}
            {/* <div className="p-4 pt-3 pb-3">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Search
              </h1>
            </div> */}
            
            {/* Search Bar */}
            <div className="px-4 pb-4 pt-3">
              <div className="flex-1 relative lg:max-w-md w-full">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                  <input
                    type="text"
                    placeholder={placeholder}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="block w-full pl-10 pr-3 py-4 border border-gray-300 dark:border-transparent rounded-full leading-5 bg-white text-sm dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 dark:focus:border-blue-400"
                    autoFocus
                  />
              </div>
            </div>

            {/* Filter Dropdown */}
            <div className="px-4 pb-4 flex-1">
              <div className="relative">
                {/* Filter Button */}
                {/* <button
                  onClick={() => {
                    if (filterInputRef.current) {
                      filterInputRef.current.focus();
                    }
                  }}
                  className="flex items-center justify-between w-full px-6 py-4 text-left text-base text-gray-900 dark:text-white leading-5 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none transition-colors duration-200 border border-gray-300 dark:border-gray-600 rounded-full bg-white dark:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <span className={`truncate text-sm ${selectedTags.length > 0 ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>
                    {getSelectedTagsText()}
                  </span>
                  <div className="flex items-center gap-2 h-5">
                    {(selectedTags.length > 0 || (showArchetypeFilter && selectedArchetype !== 'all')) && (
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          clearAllFilters();
                        }}
                        className="p-0.5 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-full transition-colors h-4 w-4 flex items-center justify-center cursor-pointer"
                      >
                        <XMarkIcon className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                      </div>
                    )}
                    <ChevronDownIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  </div>
                </button> */}

                {/* Filter Dropdown Menu */}
                <div className="absolute top-full left-0 right-0 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-2xl shadow-lg z-50 max-h-80 overflow-hidden">
                  {/* Search Input */}
                  <div className="p-3 border-b border-gray-200 dark:border-gray-600">
                    <input
                      ref={filterInputRef}
                      type="text"
                      placeholder="Search tags..."
                      value={filterSearchTerm}
                      onChange={(e) => setFilterSearchTerm(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Tags List */}
                  <div className="max-h-60 overflow-y-auto">
                    {/* Archetype Filter Section (if enabled) */}
                    {/* {showArchetypeFilter && (
                      <>
                        <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-600">
                          <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            Project Type
                          </div>
                        </div>
                        {archetypeOptions.map((arch) => {
                          const isSelected = selectedArchetype === arch.id;
                          return (
                            <button
                              key={arch.id}
                              onClick={() => toggleTag(arch.id)}
                              className={`
                                w-full flex items-center gap-3 px-3 py-3 text-left text-sm
                                hover:bg-gray-50 dark:hover:bg-gray-600
                                transition-colors duration-150
                                ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''}
                              `}
                            >
                              <span className="text-lg">{arch.icon}</span>
                              <div className="flex-1">
                                <div className={`font-medium ${isSelected ? 'text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-white'}`}>
                                  {arch.label}
                                </div>
                                {arch.description && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                    {arch.description}
                                  </div>
                                )}
                              </div>
                              {isSelected && (
                                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                              )}
                            </button>
                          );
                        })}
                        <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-600">
                          <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            Tags
                          </div>
                        </div>
                      </>
                    )} */}
                    
                    {/* Regular Tags */}
                    {filteredTags.length === 0 && (!showArchetypeFilter || filteredArchetypes.length === 0) ? (
                      <div className="p-3 text-sm text-gray-500 dark:text-gray-400 text-center">
                        No tags found
                      </div>
                    ) : (
                      filteredTags.map((tag) => {
                        const isSelected = selectedTags.includes(tag.id);
                        return (
                          <button
                            key={tag.id}
                            onClick={() => toggleTag(tag.id)}
                            className={`
                              w-full flex items-center gap-3 px-3 py-3 text-left text-sm
                              hover:bg-gray-50 dark:hover:bg-gray-600
                              transition-colors duration-150
                              ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''}
                            `}
                          >
                            <span className="text-lg">{tag.icon}</span>
                            <div className="flex-1">
                              <div className={`font-medium ${isSelected ? 'text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-white'}`}>
                                {tag.label}
                              </div>
                              {tag.description && (
                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                  {tag.description}
                                </div>
                              )}
                            </div>
                            {isSelected && (
                              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                            )}
                          </button>
                        );
                      })
                    )}
                  </div>

                  {/* Clear All Button */}
                  {(selectedTags.length > 0 || (showArchetypeFilter && selectedArchetype !== 'all')) && (
                    <div className="p-3 border-t border-gray-200 dark:border-gray-600">
                      <button
                        onClick={clearAllFilters}
                        className="w-full text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                      >
                        Clear all filters
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Fixed Bottom Buttons */}
            <div className="bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-center justify-between">
                {/* Clear all button */}
                <button
                  onClick={handleMobileClearAllClick}
                  className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 text-sm font-medium underline"
                >
                  Clear all
                </button>
                
                {/* Search button */}
                <button
                  onClick={handleMobileSearchClick}
                  className="bg-black dark:bg-white text-white dark:text-black px-6 py-3 rounded-lg font-bold text-sm hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
                >
                  Search
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default SearchUI;
